/**
 * Equipment / Components section of the process Items tab — a nested-group
 * table (category → name → line items) matching the legacy warehouse render.
 * Columns: Category/Name · Ref · From Location · From Rack · Target Location ·
 * Target Rack · Qty · Condition · Status. Built on the @ui DataTable tree mode
 * (`getSubRows`): category and name rows are depth-indented group headers that
 * toggle to reveal the detail rows underneath.
 */
import { useMemo } from 'react';
import { MapPin, ArrowRight } from 'lucide-react';
import { Chip } from '@vnetwork/ui/primitives';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import type { ProcessItem } from '@/api/capacities';
import { TableLink } from '@/components/table-link';

/** Rack-slot colors: orange = source position, green = target position. */
const ORANGE = 'var(--vn-warning-solid)';
const GREEN = 'var(--vn-success-solid)';
const muted = { color: 'var(--vn-fg-muted)' } as const;
const emphasize: React.CSSProperties = {
  fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'],
};

/** A tree node: category / name group headers carry an aggregate qty; leaf
 * `item` nodes carry the underlying ProcessItem. */
interface Node {
  id: string;
  kind: 'cat' | 'name' | 'item';
  label: string;
  qty: number;
  item?: ProcessItem;
  subRows?: Node[];
}

const qtyOf = (rows: ProcessItem[]) => rows.reduce((s, r) => s + (r.quantity || 1), 0);

export function ProcessItemsGroupedTable({
  items,
  itemType,
}: {
  items: ProcessItem[];
  itemType: 'equipment' | 'component';
}) {
  const basePath = itemType === 'equipment' ? '/capacities/equipment' : '/capacities/components';
  const nameHead = itemType === 'equipment' ? 'Equipment' : 'Component';

  const data = useMemo<Node[]>(() => {
    const g: Record<string, Record<string, ProcessItem[]>> = {};
    for (const it of items) {
      const cat = it.item?.category || 'uncategorized';
      const nm = it.item?.name || 'Unknown';
      (g[cat] ??= {});
      (g[cat][nm] ??= []).push(it);
    }
    return Object.entries(g).map(([cat, names]) => ({
      id: `cat-${cat}`,
      kind: 'cat' as const,
      label: cat.toUpperCase(),
      qty: qtyOf(Object.values(names).flat()),
      subRows: Object.entries(names).map(([nm, rows]) => ({
        id: `name-${cat}-${nm}`,
        kind: 'name' as const,
        label: nm,
        qty: qtyOf(rows),
        subRows: rows.map((row, i) => ({
          id: `item-${row.id ?? `${cat}-${nm}-${i}`}`,
          kind: 'item' as const,
          label: row.item?.name || '-',
          qty: row.quantity || 1,
          item: row,
        })),
      })),
    }));
  }, [items]);

  const columns = useMemo<ColumnDef<Node, unknown>[]>(
    () => [
      {
        id: 'label',
        header: `Category / ${nameHead} Name`,
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind === 'cat') return <span style={{ textTransform: 'uppercase' }}>{n.label}</span>;
          if (n.kind === 'name') return <span>{n.label}</span>;
          return n.item?.item?.id ? (
            <TableLink to={`${basePath}/${n.item.item.id}`} title={n.item.item.name}>
              {n.item.item.name}
            </TableLink>
          ) : (
            <span>{n.label}</span>
          );
        },
      },
      {
        id: 'ref',
        header: 'Ref',
        cell: ({ row }) => {
          if (row.original.kind !== 'item') return null;
          const it = row.original.item!;
          const ref = itemType === 'equipment' ? it.item?.service_tag || it.item?.ref : it.item?.ref;
          return <span style={emphasize}>{ref || '-'}</span>;
        },
      },
      {
        id: 'fromLocation',
        header: 'From Location',
        cell: ({ row }) => {
          if (row.original.kind !== 'item') return null;
          const srcLoc = row.original.item!.metadata?.source_location_name;
          return srcLoc ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }} title={srcLoc}>
              <MapPin size={12} style={muted} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{srcLoc}</span>
            </span>
          ) : (
            <span style={muted}>-</span>
          );
        },
      },
      {
        id: 'fromRack',
        header: 'From Rack',
        cell: ({ row }) => {
          if (row.original.kind !== 'item') return null;
          const m = row.original.item!.metadata;
          const srcRack = m?.source_rack_code;
          const srcU = m?.source_position_u;
          return srcRack || srcU ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
              {srcRack && <span style={emphasize}>{srcRack}</span>}
              {srcU ? <span style={{ color: ORANGE }}>U{srcU}</span> : null}
            </span>
          ) : (
            <span style={muted}>-</span>
          );
        },
      },
      {
        id: 'targetLocation',
        header: 'Target Location',
        cell: ({ row }) => {
          if (row.original.kind !== 'item') return null;
          const tgtRack = row.original.item!.metadata?.target_rack_code;
          return tgtRack ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', color: GREEN }}>
              <ArrowRight size={12} />
              <span style={emphasize}>Transfer</span>
            </span>
          ) : (
            <span style={muted}>-</span>
          );
        },
      },
      {
        id: 'targetRack',
        header: 'Target Rack',
        cell: ({ row }) => {
          if (row.original.kind !== 'item') return null;
          const m = row.original.item!.metadata;
          const tgtRack = m?.target_rack_code;
          const tgtU = m?.target_position_u;
          return tgtRack || tgtU ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', color: GREEN }}>
              {tgtRack && <span style={emphasize}>{tgtRack}</span>}
              {tgtU ? <span>U{tgtU}</span> : null}
            </span>
          ) : (
            <span style={muted}>-</span>
          );
        },
      },
      {
        id: 'qty',
        header: 'Qty',
        cell: ({ row }) => <span>{row.original.qty}</span>,
      },
      {
        id: 'condition',
        header: 'Condition',
        cell: ({ row }) =>
          row.original.kind === 'item' ? (
            <span style={muted}>{row.original.item!.condition || '-'}</span>
          ) : null,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          if (row.original.kind !== 'item') return null;
          const it = row.original.item!;
          return it.is_returned ? (
            <Chip tone="info">Returned</Chip>
          ) : (
            <Chip tone="neutral">{it.item?.status?.toUpperCase() || 'ACTIVE'}</Chip>
          );
        },
      },
    ],
    [nameHead, basePath, itemType],
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      getRowId={(r) => r.id}
      getSubRows={(r) => r.subRows}
      enableSorting={false}
    />
  );
}
